import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock } from 'lucide-react';

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <article className="glass-strong rounded-3xl p-8 md:p-12">
            <div className="flex items-center space-x-3 mb-8">
              <Shield className="w-8 h-8 text-green-600" />
              <h1 className="pb-2 text-4xl md:text-5xl font-bold bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
                Privacy Policy
              </h1>
            </div>

            <div className="mb-6 text-sm text-gray-600">
              <p>Last Updated: {currentDate}</p>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
              <p className="text-xl text-gray-800 font-medium">
                At Crown Jackpot, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website crown-jackpot.com. Please read this policy carefully to understand our practices regarding your personal data.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Information We Collect</h2>

              <p>
                We collect information that you provide directly to us, including when you use our contact forms, subscribe to newsletters, or communicate with us. This information may include your name, email address, telephone number, and any messages you send to us. We also collect information automatically when you visit our website, including your IP address, browser type, device information, pages visited, time spent on pages, and referral sources.
              </p>

              <p>
                We use cookies and similar tracking technologies to collect information about your browsing behavior and preferences. This helps us understand how you interact with our website and improve your experience. For detailed information about our use of cookies, please refer to our Cookie Policy.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">How We Use Your Information</h2>

              <p>
                We use the information we collect to provide, maintain, and improve our services, respond to your inquiries and requests, send you newsletters and promotional communications (with your consent), analyze website usage and trends, personalize your experience, detect and prevent fraud or abuse, comply with legal obligations, and protect our rights and the rights of our users.
              </p>

              <p>
                We may combine information we collect from different sources to better understand your needs and provide you with more relevant content and recommendations. However, we will only use your information for purposes described in this Privacy Policy or as otherwise disclosed to you at the time of collection.
              </p>

              <div className="glass p-6 rounded-2xl my-8 border-l-4 border-green-500">
                <div className="flex items-start space-x-3">
                  <Lock className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Data Security</h3>
                    <p className="text-gray-700">
                      We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Sharing Your Information</h2>

              <p>
                We do not sell, trade, or rent your personal information to third parties. We may share your information with service providers who perform services on our behalf, such as hosting providers, analytics services, email service providers, and customer support platforms. These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
              </p>

              <p>
                We may also disclose your information if required by law, court order, or governmental authority, to protect our rights or property, in connection with a business transfer or merger, or to protect the safety of our users or the public. We may share aggregated, anonymized data that does not identify you personally for research, analytics, or marketing purposes.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Third-Party Links and Services</h2>

              <p>
                Our website contains links to third-party websites, including online casinos we review and recommend. When you click on these links, you will be directed to external websites that are not operated by us. We are not responsible for the privacy practices of these third-party websites and encourage you to review their privacy policies before providing any information.
              </p>

              <p>
                We may use third-party services such as Google Analytics to analyze website traffic and user behavior. These services may collect information about your use of our website through cookies and similar technologies. Please refer to our Cookie Policy for more information about these services and how to opt out if desired.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Your Rights and Choices</h2>

              <p>
                Under UK data protection laws, including the General Data Protection Regulation (GDPR) and the Data Protection Act 2018, you have certain rights regarding your personal information. These rights include: the right to access your personal information, the right to correct inaccurate information, the right to request deletion of your information, the right to object to processing of your information, the right to restrict processing of your information, the right to data portability, and the right to withdraw consent where processing is based on consent.
              </p>

              <p>
                To exercise any of these rights, please contact us using the contact information provided at the end of this Privacy Policy. We will respond to your request within one month, though this may be extended in complex cases. We may need to verify your identity before processing your request to ensure the security of your information.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Data Retention</h2>

              <p>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it. We may retain certain information for longer periods if necessary for legal, regulatory, or business purposes.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Children's Privacy</h2>

              <p>
                Our website is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child under 18, we will take steps to delete that information promptly. If you believe we have collected information from a child, please contact us immediately.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">International Data Transfers</h2>

              <p>
                Your information may be transferred to and processed in countries outside the UK and European Economic Area (EEA). When we transfer your information internationally, we ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable data protection laws. By using our website, you consent to the transfer of your information to countries outside the UK and EEA.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Changes to This Privacy Policy</h2>

              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Contact Us</h2>

              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
              </p>

              <div className="glass p-6 rounded-2xl my-6">
                <p className="font-semibold text-gray-800 mb-2">Crown Jackpot</p>
                <p className="text-gray-700">Email: info@crown-jackpot.com</p>
                <p className="text-gray-700">Phone: +44 9296 526 586</p>
                <p className="text-gray-700">Address: London, United Kingdom</p>
              </div>

              <p>
                If you are not satisfied with our response to your privacy concerns, you have the right to lodge a complaint with the Information Commissioner's Office (ICO), the UK's data protection authority.
              </p>
            </div>
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

